import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { leadService } from '../services/api';
import { Spinner } from '../components/Spinner';
import { DrillDownPanel } from '../components/reports/DrillDownPanel';
import { useDrillDown } from '../hooks/useDrillDown';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { RangePicker, RangeNote, BLANK_RANGE_FILTERS, drillDates } from '../components/reports/ReportRange';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const DrillTrigger = ({ value, active, onClick, className = '' }) => {
  if (!value) return <span className={className}>{value || 0}</span>;
  return (
    <button onClick={onClick} className={`hover:underline font-medium ${active ? 'underline' : ''} ${className}`}>
      {value}
    </button>
  );
};

// DPS-style column set — each maps to a real, existing schema field so every
// number here is drillable to the exact matching leads (no fabricated metrics).
const COLS = [
  { key: 'totalLeads',       label: 'Total Enquiries', params: {},                                                cls: 'text-gray-700' },
  { key: 'bookings',         label: 'Bookings',        params: { convertedSubType: 'booked' },                     cls: 'text-blue-700' },
  { key: 'converted',        label: 'Converted',       params: { status: 'converted' },                            cls: 'text-emerald-700' },
  { key: 'lost',             label: 'Lost Leads',      params: { status: 'lost' },                                 cls: 'text-red-600' },
  { key: 'lostToCoDealer',   label: 'Lost to Co-Dealer', params: { status: 'lost', activitySubTypeKey: 'lost_to_codealers' },    cls: 'text-orange-600' },
  { key: 'lostToCompetitor', label: 'Lost to Competitor', params: { status: 'lost', activitySubTypeKey: 'lost_to_competitions' }, cls: 'text-orange-600' },
  { key: 'notPurchased',     label: 'Not Purchased',   params: { status: 'lost', activitySubTypeKey: 'not_purchased' },          cls: 'text-orange-600' },
  { key: 'duplicateLeads',   label: 'Duplicate Leads', params: { duplicates: 'true' },                             cls: 'text-purple-600' },
];

export const GroupReport = () => {
  const [filters, setFilters] = useState(BLANK_RANGE_FILTERS);
  const [applied, setApplied] = useState(BLANK_RANGE_FILTERS);

  const { data, isLoading } = useQuery(
    ['groupReport', applied],
    () => leadService.getGroupReport(applied),
    { keepPreviousData: true }
  );

  // The window the API actually used — drives the note and the drill-downs.
  const appliedRange = data?.data?.data?.range;
  const rows = data?.data?.data?.rows || [];
  const grandTotal = data?.data?.data?.grandTotal;

  const drill = useDrillDown(leadService.getLeads);
  const { expanded, close, search, setSearch, leads, total, loading: drillLoading, loadingMore, hasMore, loadMore } = drill;

  const toggle = (row, colKey, label, colParams) => {
    const rowKey = `${row.groupId}_${row.filterType}`;
    const filterParam = row.filterType === 'dealer' ? { dealerId: row.filterId } : { assignedTo: row.filterId };
    drill.toggle(
      `${rowKey}_${colKey}`,
      `${row.groupName} — ${label}`,
      { ...filterParam, ...drillDates(appliedRange), ...colParams }
    );
  };

  const chartData = rows.slice(0, 10).map(r => ({ name: r.groupName, Total: r.totalLeads, Converted: r.converted, Lost: r.lost }));
  const colSpan = COLS.length + 4; // + Dealer/Branch, Active, Conv%Closed, Conv%Overall

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6 text-primary-600" />
          Group Report
        </h1>
        <p className="mt-1 text-gray-600">
          Lead performance by dealer/branch in your workspace — enquiries, bookings, lost breakdown, duplicates and conversion rates.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-4 px-5 py-4 flex flex-wrap items-center gap-2">
        <RangePicker value={filters} onChange={setFilters} />
        <button onClick={() => { setApplied(filters); close(); }} className="btn-primary text-sm">Search</button>
        <button onClick={() => { setFilters(BLANK_RANGE_FILTERS); setApplied(BLANK_RANGE_FILTERS); close(); }} className="btn-outline-secondary text-sm">Clear Filter</button>
        <div className="w-full"><RangeNote applied={appliedRange} /></div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">No data for this period.</div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Dealer/branch comparison</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total" fill="#ef6c1a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Converted" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Lost" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table — DPS Group Report style: one row per dealer/branch, granular breakdown */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Dealer / Branch</th>
                    {COLS.map(c => (
                      <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Active (WIP)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Conv. % (Closed)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Conv. % (Overall)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const rowKey = `${r.groupId}_${r.filterType}`;
                    const rowExpanded = expanded?.key?.startsWith(`${rowKey}_`);
                    const panelProps = rowExpanded ? {
                      title: expanded.title,
                      leads, total, isLoading: drillLoading, onClose: close,
                      search, onSearchChange: setSearch,
                      hasMore, onLoadMore: loadMore, loadingMore,
                    } : null;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{r.groupName}</td>
                          {COLS.map(c => (
                            <td key={c.key} className="px-4 py-3">
                              <DrillTrigger
                                value={r[c.key]}
                                active={expanded?.key === `${rowKey}_${c.key}`}
                                className={c.cls}
                                onClick={() => toggle(r, c.key, c.label, c.params)}
                              />
                            </td>
                          ))}
                          <td className="px-4 py-3 text-gray-700">{r.active}</td>
                          <td className="px-4 py-3 text-gray-700">{r.conversionOnClosed}%</td>
                          <td className="px-4 py-3 text-gray-700">{r.conversionOnOverall}%</td>
                        </tr>
                        {panelProps && <DrillDownPanel colSpan={colSpan} {...panelProps} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {grandTotal && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">Grand Total</td>
                      {COLS.map(c => (
                        <td key={c.key} className={`px-4 py-3 font-bold ${c.cls}`}>{grandTotal[c.key]}</td>
                      ))}
                      <td className="px-4 py-3 font-bold text-gray-900">{grandTotal.active}</td>
                      <td className="px-4 py-3 font-bold text-gray-900">{grandTotal.conversionOnClosed}%</td>
                      <td className="px-4 py-3 font-bold text-gray-900">{grandTotal.conversionOnOverall}%</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
